import type { MessageTransport } from '@speles7172/message-console';
import type { ThreadMessage } from '@speles7172/message-client/core';
import type { UploadLinkConfig } from '@peles/shared';
import { apiClient } from './client';
import {
  attachLibraryFiles,
  fetchThreadAttachments,
  removeThreadAttachment,
  uploadFileToThread,
} from './threadTools';

/**
 * The seam between `@speles7172/message-console` and this application's API
 * (COMMUNICATIONS-DESIGN D6).
 *
 * ⚠️ **Hold the transport still.** Every component keys its effects on the
 * object's identity, so an object literal written inline in JSX is a new
 * transport on every render and therefore an endless reload. A module constant
 * for exactly that reason — the same rule `api/files.ts` follows, and the one
 * documented on every console in this repository.
 *
 * ⚠️ **Every method is a capability, and the omissions here are deliberate.**
 * A transport with no `postMessage` renders a read-only transcript; one with no
 * `people` renders no participant picker and no `@` autocomplete. So this
 * exports what the endpoints actually back, and nothing else: a button that
 * always fails is worse than a button that is not there.
 *
 * Not here yet, because no endpoint answers them: `templates`, `groups`. The
 * template editor and the routing console are D-side administration and land
 * with their own endpoints.
 *
 * ⚠️ **Attachments are on `createRecordTransport` below, not here.** They need
 * no scope of their own — every method takes the thread id — but the *device
 * upload* does: the server files it under the conversation's record, and a
 * conversation about nothing has nowhere to put it. Rather than offer a
 * dropzone that answers 400, the capability is granted by the factory, which
 * knows whether the record has a folder. A button that always fails is worse
 * than a button that is not there — the rule this file already follows.
 */
/**
 * Posting one message, with whatever it is meant to carry.
 *
 * ⚠️ **One place, because the staged file request is not part of the package's
 * call.** `<ThreadPanel>` calls `transport.postMessage({ threadId, body })` and
 * knows nothing else; the request has to be added by whoever holds it. Both the
 * plain transport and the record one route through here so the body's shape is
 * decided once.
 */
async function postThreadMessage(
  input: { threadId: string; body: string },
  uploadRequest: UploadLinkConfig | null,
): Promise<ThreadMessage> {
  const { data } = await apiClient.post(`/threads/${input.threadId}/messages`, {
    body: input.body,
    ...(uploadRequest === null ? {} : { uploadRequest }),
  });
  return data as ThreadMessage;
}

export const messageTransport: MessageTransport = {
  async notifications(query) {
    const { data } = await apiClient.get('/notifications', {
      params: {
        ...(query.unreadOnly ? { unread: 'true' } : {}),
        ...(query.search ? { term: query.search } : {}),
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
        ...(query.offset !== undefined ? { offset: query.offset } : {}),
      },
    });
    return data;
  },

  async unreadCount() {
    const { data } = await apiClient.get<{ count: number }>('/notifications/unread');
    return data.count;
  },

  async markRead(ids) {
    await apiClient.post('/notifications/read', { ids });
  },

  async markAllRead() {
    await apiClient.post('/notifications/read-all');
  },

  async removeNotification(id) {
    await apiClient.delete(`/notifications/${id}`);
  },

  async preferences() {
    const { data } = await apiClient.get('/notifications/preferences');
    return data;
  },

  async setPreference(change) {
    // One toggle per request, not the whole object: a page that PUTs everything
    // loses every change made in another tab.
    const { data } = await apiClient.put('/notifications/preferences', change);
    return data;
  },

  async threads(query) {
    const { data } = await apiClient.get('/threads', {
      params: {
        ...(query.entityType ? { entityType: query.entityType } : {}),
        ...(query.entityId ? { entityId: query.entityId } : {}),
        ...(query.statuses?.length ? { statuses: query.statuses.join(',') } : {}),
        ...(query.search ? { term: query.search } : {}),
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
        ...(query.offset !== undefined ? { offset: query.offset } : {}),
      },
    });
    return data;
  },

  async thread(threadId) {
    const { data } = await apiClient.get(`/threads/${threadId}`);
    return data;
  },

  async startThread(input) {
    const { data } = await apiClient.post('/threads', input);
    return data.thread ?? data;
  },

  postMessage: (input) => postThreadMessage(input, null),

  async setThreadStatus({ threadId, status }) {
    const { data } = await apiClient.put(`/threads/${threadId}/status`, { status });
    return data;
  },

  async addParticipants({ threadId, recipientIds }) {
    await apiClient.post(`/threads/${threadId}/participants`, { recipientIds });
  },

  async markThreadRead(threadId) {
    await apiClient.post(`/threads/${threadId}/read`);
  },

  /**
   * Who can be added to a conversation.
   *
   * ⚠️ **Its presence is what draws the participant picker and the `@`
   * autocomplete.** Without it the thread panel renders neither — which would
   * leave a conversation nobody can add anyone to.
   */
  people: peopleTransport(),

  /**
   * The files a conversation carries.
   *
   * ⚠️ **On the base transport, because a file belongs to the *thread* and not
   * to whichever screen is showing it.** These lived on `createRecordTransport`
   * alone, so the same conversation opened from an event listed its three
   * reports and the same conversation opened from `/communications` listed
   * none — the identical message, drawn two different ways, depending on the
   * route somebody happened to arrive by.
   *
   * Every one of them is keyed by thread id and authorised server-side against
   * the thread's own membership, so none of them needs a record scope. What
   * `createRecordTransport` still adds is the *people* picker, which genuinely
   * does: who may be added depends on the record.
   */
  threadAttachments: (threadId) => fetchThreadAttachments(threadId),
  removeAttachment: ({ threadId, attachmentId }) => removeThreadAttachment(threadId, attachmentId),
};

/**
 * The same transport, plus a drop target.
 *
 * ⚠️ **Upload is the one attachment capability that is not unconditional.**
 * Listing and removing are keyed by thread id and answer for themselves;
 * uploading needs the thread to be *about* something, because the server
 * resolves the destination folder from the record. A conversation attached to
 * no record has nowhere to put a file, and a dropzone that answers 400 is worse
 * than no dropzone — so the caller says whether it has one.
 */
export function withThreadUpload(base: MessageTransport = messageTransport): MessageTransport {
  return {
    ...base,
    async attachToThread({ threadId, file }) {
      const attached = await uploadFileToThread(threadId, file);
      if (attached === null) throw new Error('The file could not be attached.');
      return attached;
    },
  };
}

/**
 * Who can be added to a conversation, optionally narrowed to a record.
 *
 * ⚠️ **The scope changes what the list *means*, not only its length.** Unscoped
 * it is every account the caller may see, described by an email address. Given
 * an event it is the people actually on that event, each described by what puts
 * them there — "Vendor · Bella Catering", "Client", "Venue · The Grand" — which
 * is the question an address cannot answer and the one somebody adding a
 * recipient is actually asking. The server resolves the capacity; see
 * `access/event-people.ts`.
 */
function peopleTransport(scope?: RecordScope, threadId?: string): MessageTransport['people'] {
  const params = {
    ...(scope ? { entityType: scope.entityType, entityId: scope.entityId } : {}),
    // So the picker stops offering the people who are already here. Absent
    // until a conversation is open, which is also when there is nobody to
    // exclude.
    ...(threadId ? { threadId } : {}),
  };
  return {
    async search(query) {
      const { data } = await apiClient.get('/options/people', {
        params: {
          ...params,
          ...(query.term ? { term: query.term } : {}),
          ...(query.limit !== undefined ? { limit: query.limit } : {}),
          ...(query.offset !== undefined ? { offset: query.offset } : {}),
        },
      });
      return data;
    },

    async resolve(values) {
      const { data } = await apiClient.get('/options/people/resolve', {
        params: { ...params, values: values.join(',') },
      });
      return data.options;
    },
  };
}

/** The record a conversation is about, as the pickers need to know it. */
export interface RecordScope {
  entityType: string;
  entityId: string;
}

/**
 * The transport for a conversation hanging off one record.
 *
 * `scope` is `null` for a conversation about no record: the picker then
 * searches everybody, as the inbox's own transport does.
 *
 * ⚠️ **Built per record, and the caller must hold it still.** Every component
 * in `message-console` keys its effects on the transport's identity, so this
 * belongs in a `useMemo` on `entityType`/`entityId` — an object literal in JSX
 * is a new transport every render and therefore an endless reload. That is the
 * same rule the module constant above exists to satisfy; a scoped transport
 * cannot be a module constant, so the rule moves to its caller.
 *
 * ⚠️ **`attachToThread` is granted, not always present.** Its absence is what
 * turns the dropzone off, and a conversation whose record has no folder has
 * nowhere to file an upload. `canUpload` is the caller saying it has somewhere
 * — which it learns from `GET /threads/:id/context`.
 *
 * ⚠️ **`threadId` is what stops the picker offering the people already on the
 * conversation.** Without it "Add people" lists them too, and every one of
 * those rows does nothing — which on a small team is every row in the list.
 */
export function createRecordTransport(
  scope: RecordScope | null,
  options: {
    canUpload?: boolean;
    threadId?: string;
    /**
     * A file request staged to go out with the next message.
     *
     * ⚠️ **Staged rather than sent, because the message box is not ours.** The
     * composer belongs to `<ThreadPanel>`, so a toolbar beside it cannot add
     * anything to what the writer is typing. Asking for a file therefore used
     * to post a message of its own — and the recipient got two emails about one
     * act: the words in one, the button in the other. Held here instead, it
     * travels with the next `postMessage` and arrives as a single email
     * carrying the words, the files and that person's own link.
     */
    uploadRequest?: UploadLinkConfig | null;
    /** Called once a message has carried the staged request, so it can clear. */
    onUploadRequestSent?: () => void;
  } = {},
): MessageTransport {
  /*
   * Listing and removing a conversation's files come from the base transport:
   * they belong to the thread, not to the screen. What a record scope decides
   * is who may be added, and whether this screen can offer a drop target.
   */
  const base = options.canUpload === true ? withThreadUpload() : messageTransport;
  return {
    ...base,
    // No record — a conversation about nothing, opened from the inbox — still
    // excludes the people already on it, and still carries a staged request.
    people: peopleTransport(scope ?? undefined, options.threadId),
    async postMessage(input) {
      const staged = options.uploadRequest;
      const posted = await postThreadMessage(input, staged ?? null);
      // Only once it has actually gone: a failed send must leave the request
      // staged, or the writer's next attempt quietly drops it.
      if (staged) options.onUploadRequestSent?.();
      return posted;
    },
  };
}

/** Attach files chosen out of the library, then let the panel reload. */
export { attachLibraryFiles };

/**
 * The options half of the transport, on its own.
 *
 * `NewConversationModal` needs the people picker before a thread exists, so it
 * cannot take one off a thread-scoped transport. Same endpoint, same scoping,
 * same rule about holding the object still — see `peopleTransport`.
 */
export function peopleOptions(scope?: RecordScope): NonNullable<MessageTransport['people']> {
  return peopleTransport(scope) as NonNullable<MessageTransport['people']>;
}

/** One conversation on a record, as the switcher lists them. */
export interface RecordThread {
  id: string;
  subject: string | null;
  status: string;
  lastMessageAt?: string | null;
  createdAt?: string;
  participants: { recipientId: string; name: string | null }[];
}

/** A page of a record's conversations, and whether another follows it. */
export interface RecordThreadPage {
  items: RecordThread[];
  /**
   * Whether the record has rows past this page.
   *
   * ⚠️ **Deliberately not a total.** The server narrows a page to the
   * conversations this caller may join, so a total would say how many they are
   * being kept out of — a count of other people's private conversations on a
   * record they can merely reach. The server answers this from the page before
   * admission narrowed it, which is what keeps the walk going: counting the
   * survivors would stop it the first time a row was filtered out.
   */
  hasMore: boolean;
  /**
   * The window the server actually served.
   *
   * ⚠️ **This is what makes the next page reachable.** The rows come back
   * narrowed, so a caller cannot tell where the next page starts by counting
   * what it received — `offset + limit` can.
   */
  limit: number;
  offset: number;
}

/**
 * A page of a record's conversations, most recently active first.
 *
 * ⚠️ **Not `threads({ entityType, entityId })`.** That list is scoped to the
 * caller's own memberships, so a producer left off one sees fewer than exist
 * and starts a duplicate — the same reason `fetchRecordThreadId` exists. This
 * asks by record; the server decides per conversation whether the caller may
 * join it.
 */
export async function fetchRecordThreads(
  entityType: string,
  entityId: string,
  options: { limit?: number; offset?: number } = {},
): Promise<RecordThreadPage> {
  const { data } = await apiClient.get('/threads/for-record/list', {
    params: {
      entityType,
      entityId,
      ...(options.limit !== undefined ? { limit: options.limit } : {}),
      ...(options.offset ? { offset: options.offset } : {}),
    },
  });
  const items = (data?.items ?? []) as RecordThread[];
  const num = (value: unknown, fallback: number) =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return {
    items,
    hasMore: data?.hasMore === true,
    // Falling back to what was asked for, so a server that answers without the
    // envelope still leaves the caller able to ask for the page after this one.
    limit: num(data?.limit, options.limit ?? items.length),
    offset: num(data?.offset, options.offset ?? 0),
  };
}

/**
 * Open a new conversation.
 *
 * ⚠️ **`record` may be null, and that is a conversation about nothing.** The
 * conversations page can start one that belongs to no event — a call with a
 * venue about next season — and the endpoint has always allowed it. Sending
 * `null` says so; omitting the keys would let the server keep whatever default
 * it has.
 */
/**
 * The opening message on a conversation that was just started.
 *
 * ⚠️ **`POST /threads` cannot carry one, and that is a fact about the package.**
 * `startThread` creates the thread, adds its participants, and then posts the
 * body through `postMessage` **without** `attachments` or `perRecipient` — so an
 * opening message written that way can never carry a file or an upload button,
 * however early they were staged. Worse, its email goes out before a caller has
 * had the chance to attach anything at all.
 *
 * So the dialog starts the thread empty and says the first thing through this,
 * which is the ordinary message path and already carries both. A thread that
 * starts with no message is a state the package declares outright ("a thread may
 * legitimately start empty") and `startThread` returns `message: null` for it —
 * no notification is sent for the creation itself, so nothing is emailed twice.
 */
export async function postOpeningMessage(
  threadId: string,
  body: string,
  uploadRequest: UploadLinkConfig | null,
): Promise<ThreadMessage & { requested?: number }> {
  /*
   * ⚠️ **`requested` is not decoration — it is how a refused ask is noticed.**
   * The endpoint posts the message whether or not the links could be minted:
   * nobody to email, or a folder that is not the record's, must not cost
   * somebody the words they wrote. So the message succeeds, `201` comes back,
   * and the only thing that says the button never went out is this count. A
   * caller that ignores it tells the sender their request was sent when nobody
   * received one.
   */
  return postThreadMessage({ threadId, body }, uploadRequest) as Promise<
    ThreadMessage & { requested?: number }
  >;
}

export async function startConversation(input: {
  subject?: string | undefined;
  body?: string | undefined;
  recipientIds: string[];
  record: RecordScope | null;
}): Promise<{ id: string }> {
  const { data } = await apiClient.post('/threads', {
    ...(input.subject !== undefined ? { subject: input.subject } : {}),
    ...(input.body !== undefined ? { body: input.body } : {}),
    entityType: input.record?.entityType ?? null,
    entityId: input.record?.entityId ?? null,
    participantIds: input.recipientIds,
  });
  return (data?.thread ?? data) as { id: string };
}

/**
 * The conversation a record already has, if any.
 *
 * ⚠️ **Not `threads({ entityType, entityId })`.** That list is scoped to the
 * caller's own memberships, so a producer left off an enquiry's participants
 * sees nothing — and a caller that reads "nothing" as "start one" opens a
 * second thread on an enquiry already being answered. This asks by record.
 */
export async function fetchRecordThreadId(
  entityType: string,
  entityId: string,
): Promise<string | null> {
  const { data } = await apiClient.get('/threads/for-record', {
    params: { entityType, entityId },
  });
  return data?.threadId ?? null;
}

/** How many threads each of these records has, for a list page. */
/** What the endpoint will answer about in one call. */
const COUNTS_PER_REQUEST = 200;

export async function fetchThreadCounts(
  entityType: string,
  entityIds: string[],
): Promise<Record<string, { total: number; unread: number }>> {
  if (entityIds.length === 0) return {};

  /*
   * ⚠️ **Chunked, not truncated.** The endpoint answers about 200 records at a
   * time, and an earlier version simply sliced to the first 200 — so on a
   * longer list every later record was reported as having no conversation,
   * which is indistinguishable from having none. A caller cannot tell a
   * silently dropped id from a genuine zero, so this does not create any.
   */
  const chunks: string[][] = [];
  for (let i = 0; i < entityIds.length; i += COUNTS_PER_REQUEST) {
    chunks.push(entityIds.slice(i, i + COUNTS_PER_REQUEST));
  }

  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const { data } = await apiClient.get('/threads/counts', {
        params: { entityType, entityIds: chunk.join(',') },
      });
      return data as Record<string, { total: number; unread: number }>;
    }),
  );

  return Object.assign({}, ...pages);
}

/** An event a person can turn on or off, as `GET /notifications/events` answers it. */
export interface PreferenceEvent {
  key: string;
  label: string;
  description?: string;
  channels: ('in_app' | 'email')[];
}

/** The events the application raises — the rows the preferences screen offers. */
export async function fetchPreferenceEvents(): Promise<PreferenceEvent[]> {
  const { data } = await apiClient.get<PreferenceEvent[]>('/notifications/events');
  return data;
}

/** What conversations can do in this deployment. */
export interface ThreadCapabilities {
  /** Whether a reply to an email arrives back in its conversation. */
  replyByEmail: boolean;
}

export async function fetchThreadCapabilities(): Promise<ThreadCapabilities> {
  const { data } = await apiClient.get<ThreadCapabilities>('/threads/capabilities');
  return data;
}
